"use client";

import React, { useEffect, useState } from "react";

type InviteRow = {
  id: number;
  role: "viewer" | "editor";
  expires_at: string;
  created_at: string;
  invite_token?: string | null;
};

type CreateInviteRes = {
  ok: boolean;
  invite_id: number;
  invite_token: string;
  role: "viewer" | "editor";
  expires_at: string;
};

export default function ShareInvitesModal(props: {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  projectPublicId?: string | null;
  sku: string;
  locale: string;
  // English comment: API helpers that already include Authorization header.
  apiGet: <T>(path: string) => Promise<T>;
  apiPost: <T>(path: string, body: any) => Promise<T>;
  apiDelete: <T>(path: string) => Promise<T>;
  // English comment: If false, Share UI is disabled/hidden for guests.
  canShare: boolean;
}) {
  const { isOpen, onClose, projectId, projectPublicId, sku, locale, apiGet, apiPost, apiDelete, canShare } = props;
  const [role, setRole] = useState<"viewer" | "editor">("editor");
  const [expiresDays, setExpiresDays] = useState<number>(7);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  // English comment: Store invite URLs returned by the API so links can be copied after refresh.
  const [inviteUrlById, setInviteUrlById] = useState<Record<number, string>>({});
  const lang = locale === "pl" ? "pl" : "en";
  const text = {
    title: lang === "pl" ? "Udostępnianie" : "Share",
    close: lang === "pl" ? "Zamknij" : "Close",
    noPermission: lang === "pl"
      ? "Nie masz uprawnień do udostępniania projektu."
      : "You do not have permission to share this project.",
    createInvite: lang === "pl" ? "Utwórz zaproszenie" : "Create invite",
    viewer: lang === "pl" ? "Oglądający" : "Viewer",
    editor: lang === "pl" ? "Edytujący" : "Editor",
    expires: lang === "pl" ? "Wygasa" : "Expires",
    oneDay: lang === "pl" ? "1 dzień" : "1 day",
    twoDays: lang === "pl" ? "2 dni" : "2 days",
    sevenDays: lang === "pl" ? "7 dni" : "7 days",
    fourteenDays: lang === "pl" ? "14 dni" : "14 days",
    creating: lang === "pl" ? "Tworzenie..." : "Creating...",
    createLink: lang === "pl" ? "Utwórz link" : "Create link",
    inviteLink: lang === "pl" ? "Link zaproszenia" : "Invite link",
    copy: lang === "pl" ? "Kopiuj" : "Copy",
    activeInvites: lang === "pl" ? "Aktywne zaproszenia" : "Active invites",
    refresh: lang === "pl" ? "Odśwież" : "Refresh",
    loading: lang === "pl" ? "Ładowanie..." : "Loading...",
    noInvites: lang === "pl" ? "Brak zaproszeń." : "No invites yet.",
    copyLink: lang === "pl" ? "Kopiuj link" : "Copy link",
    revoke: lang === "pl" ? "Cofnij dostęp" : "Revoke",
    tokenMissing: lang === "pl" ? "Token niedostępny. Utwórz nowe zaproszenie." : "Token not available. Create a new invite.",
    expired: lang === "pl" ? "wygasło" : "expired",
  };

  // English comment: Build editor invite URL using current origin + locale-aware path if applicable.
  const buildInviteUrl = (inviteToken: string) => {
    const base = (process.env.NEXT_PUBLIC_EDITOR_BASE_PUBLIC as string) || window.location.origin;
    const hasLocalePrefix = window.location.pathname.startsWith(`/${locale}/`);
    const editorPath = hasLocalePrefix
      ? `/${locale}/editor/${encodeURIComponent(sku)}`
      : `/editor/${encodeURIComponent(sku)}`;
    const params = new URLSearchParams();
    if (projectPublicId) params.set("project", projectPublicId);
    params.set("invite", inviteToken);
    return `${base}${editorPath}?${params.toString()}`;
  };

  async function refreshInvites() {
    setLoading(true);
    setError(null);
    try {
      const rows = await apiGet<InviteRow[]>(`projects/${projectId}/invites`);
      const list = Array.isArray(rows) ? rows : [];
      setInvites(list);
      const withUrls: Record<number, string> = {};
      for (const inv of list) {
        if (inv.invite_token) {
          withUrls[inv.id] = buildInviteUrl(inv.invite_token);
        }
      }
      setInviteUrlById(withUrls);
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }

  async function createInvite() {
    setCreating(true);
    setError(null);
    try {
      const res = await apiPost<CreateInviteRes>(`projects/${projectId}/invites`, {
        role,
        expires_days: expiresDays
      });
      const url = buildInviteUrl(res.invite_token);
      setInviteUrlById((m) => ({ ...m, [res.invite_id]: url }));
      setLastInviteUrl(url);
      await refreshInvites();
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // English comment: Clipboard can fail in insecure contexts or browser privacy modes.
      }
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setCreating(false);
    }
  }

  async function revokeInvite(inviteId: number) {
    setError(null);
    try {
      await apiDelete(`projects/${projectId}/invites/${inviteId}`);
      setInviteUrlById((m) => {
        const next = { ...m };
        delete next[inviteId];
        return next;
      });
      await refreshInvites();
    } catch (e: any) {
      setError(String(e?.message || e));
    }
  }

  useEffect(() => {
    if (!isOpen || !canShare) return;
    refreshInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId, canShare]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[11000]">
      {/* English comment: Backdrop does not close on click (close only via X/Esc). */}
      <div className="absolute inset-0 bg-black/40" />
      <div className="fixed right-0 top-[72px] z-[11010] h-[calc(100vh-72px)] w-[430px] max-w-[92vw] overflow-hidden border-l border-t-[3px] border-white/10 bg-[#0f141d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="text-sm font-semibold uppercase tracking-wide text-white">{text.title}</div>
          <button
            type="button"
            className="rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
            onClick={onClose}
            aria-label={text.close}
            title={text.close}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="max-h-[calc(100vh-160px)] space-y-4 overflow-auto p-4">
          {!canShare ? (
            <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-white/80">
              {text.noPermission}
            </div>
          ) : null}

          {canShare ? (
            <>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-sm font-semibold text-white">{text.createInvite}</div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      role === "viewer"
                        ? "border-white/30 bg-white/10 text-white"
                        : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
                    }`}
                    onClick={() => setRole("viewer")}
                  >
                    {text.viewer}
                  </button>
                  <button
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      role === "editor"
                        ? "border-white/30 bg-white/10 text-white"
                        : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
                    }`}
                    onClick={() => setRole("editor")}
                  >
                    {text.editor}
                  </button>
                </div>

                <div className="mt-3">
                  <label className="text-xs text-white/70">{text.expires}</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-white/10 bg-[#0f141d] px-3 py-2 text-sm text-white"
                    value={expiresDays}
                    onChange={(e) => setExpiresDays(Number(e.target.value))}
                  >
                    <option value={1}>{text.oneDay}</option>
                    <option value={2}>{text.twoDays}</option>
                    <option value={7}>{text.sevenDays}</option>
                    <option value={14}>{text.fourteenDays}</option>
                  </select>
                </div>

                <button
                  type="button"
                  className="mt-3 w-full rounded-lg bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-white/90 disabled:opacity-50"
                  disabled={creating}
                  onClick={createInvite}
                >
                  {creating ? text.creating : text.createLink}
                </button>

                {lastInviteUrl ? (
                  <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3">
                    <div className="text-xs text-white/70">{text.inviteLink}</div>
                    <div className="mt-1 break-all text-xs text-white">{lastInviteUrl}</div>
                    <button
                      type="button"
                      className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(lastInviteUrl);
                        } catch {
                          // English comment: Clipboard can fail silently in some browsers.
                        }
                      }}
                    >
                      {text.copy}
                    </button>
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">{text.activeInvites}</div>
                  <button
                    type="button"
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
                    disabled={loading}
                    onClick={refreshInvites}
                  >
                    {text.refresh}
                  </button>
                </div>

                {loading ? <div className="mt-3 text-sm text-white/70">{text.loading}</div> : null}
                {!loading && invites.length === 0 ? (
                  <div className="mt-3 text-sm text-white/70">{text.noInvites}</div>
                ) : null}

                <div className="mt-3 space-y-2">
                  {invites.map((inv) => {
                    const expired = new Date(inv.expires_at).getTime() < Date.now();
                    const knownUrl = inviteUrlById[inv.id] || null;
                    return (
                      <div key={inv.id} className="rounded-lg border border-white/10 bg-black/20 p-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm text-white">
                              #{inv.id} · {inv.role === "viewer" ? text.viewer : text.editor}
                            </div>
                            <div className="mt-1 text-xs text-white/70">
                              {text.expires.toLowerCase()}: {new Date(inv.expires_at).toLocaleString(locale)}
                              {expired ? ` · ${text.expired}` : ""}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
                              disabled={!knownUrl}
                              title={knownUrl ? text.copyLink : text.tokenMissing}
                              onClick={async () => {
                                if (!knownUrl) return;
                                try {
                                  await navigator.clipboard.writeText(knownUrl);
                                } catch {
                                  // English comment: Clipboard can fail silently in some browsers.
                                }
                              }}
                            >
                              {text.copyLink}
                            </button>
                            <button
                              type="button"
                              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10 disabled:opacity-50"
                              onClick={() => revokeInvite(inv.id)}
                            >
                              {text.revoke}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {error ? (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200">
                  {error}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
